import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusCircle } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import ErrorCard from '../../ui/ErrorCard';
import Modal from '../../ui/Modal';
import { DateTimeText, Id, Mono, Sku } from '../../ui/Mono';
import Select from '../../ui/Select';
import { SkeletonTable } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import { ExtractPaging } from '../../../utils/aggregation';
import { ExtractListRows } from '../payload';
import ProductionOrderCreateModal from './ProductionOrderCreateModal';
import ProductionOrderDetailPanel from './ProductionOrderDetailPanel';
import { GetProductionOrdersSummary, ListMachines, ListProductionOrders } from './productionApi';
import { FormatLabel, useProductionLabels } from './productionLabels';
import { PRODUCTION_ORDER_STATUSES, ProductionOrderStatusVariant } from './productionModel';

const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [15, 30, 50, 100];
const BLANK_CELL = <span className="text-mono-400">—</span>;
// O painel se atualiza sozinho: o gestor acompanha o chão sem recarregar.
const SUMMARY_REFRESH_MS = 30000;

/**
 * O PAINEL das OPs em aberto, no topo da tabela: quantas abertas (planejadas ×
 * liberadas), quantas rodando numa máquina, kits apontados (e o avanço contra
 * a meta, nas que têm meta) e o dia (kits apontados e OPs finalizadas).
 */
const ProductionOrdersSummary = ({ summary, label }) => {
    if (!summary) {
        return null;
    }
    const target = summary.target ?? {};
    const pct = target.kits_planned > 0 ? Math.round((100 * target.kits_done) / target.kits_planned) : null;
    const card = 'rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950';
    const title = 'text-xs font-semibold uppercase tracking-wide text-mono-500';
    const value = 'mt-1 text-3xl font-black tabular-nums text-black dark:text-white';
    const sub = 'mt-1 text-xs text-mono-600 dark:text-mono-400';
    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label={label('wms.production.summary_title')}>
            <div className={card}>
                <p className={title}>{label('wms.production.summary_open')}</p>
                <p className={value}>{summary.open_orders}</p>
                <p className={sub}>
                    {FormatLabel(label('wms.production.summary_open_split'), {
                        planned: summary.planned,
                        released: summary.released,
                    })}
                </p>
            </div>
            <div className={card}>
                <p className={title}>{label('wms.production.summary_running')}</p>
                <p className={value}>{summary.at_machine}</p>
                <p className={sub}>{label('wms.production.summary_running_sub')}</p>
            </div>
            <div className={card}>
                <p className={title}>{label('wms.production.summary_kits')}</p>
                <p className={value}>{summary.kits_open}</p>
                {pct === null ? (
                    <p className={sub}>{label('wms.production.summary_no_target')}</p>
                ) : (
                    <>
                        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-mono-100 dark:bg-mono-800">
                            <div className="h-full rounded-full bg-success-600" style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <p className={sub}>
                            {FormatLabel(label('wms.production.summary_target'), {
                                done: target.kits_done,
                                planned: target.kits_planned,
                                pct,
                                orders: target.orders,
                            })}
                        </p>
                    </>
                )}
            </div>
            <div className={card}>
                <p className={title}>{label('wms.production.summary_today')}</p>
                <p className={value}>{summary.kits_today}</p>
                <p className={sub}>
                    {FormatLabel(label('wms.production.summary_today_sub'), { finished: summary.finished_today })}
                </p>
            </div>
        </div>
    );
};

/**
 * ABA "ORDENS DE PRODUÇÃO" — painel das OPs em aberto no topo, a lista, e a
 * ficha num MODAL ao clicar na linha (pedido do dono: a coluna lateral
 * apertava a tabela).
 *
 * Filtros de STATUS e MÁQUINA vão para o SERVIDOR (é o que a rota aceita:
 * `status`, `machine_code`, `limit`, `offset`). A busca textual da tabela é
 * client-side porque a rota de OPs não tem busca — e inventar uma busca no
 * cliente sobre uma página do servidor mentiria sobre o universo pesquisado;
 * por isso ela fica no modo padrão da StandardTable (busca dentro do que está
 * carregado) e a paginação continua sendo a de verdade, do servidor.
 */
const ProductionOrdersManager = ({ onOpenLot }) => {
    const { label } = useProductionLabels();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [status, setStatus] = useState('');
    const [machineCode, setMachineCode] = useState('');
    const [machines, setMachines] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [isCreateOpen, setCreateOpen] = useState(false);
    const [summary, setSummary] = useState(null);

    const loadSummary = useCallback(() => {
        GetProductionOrdersSummary()
            .then((data) => setSummary(data ?? null))
            .catch(() => {});
    }, []);

    useEffect(() => {
        loadSummary();
        const timer = window.setInterval(loadSummary, SUMMARY_REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [loadSummary]);

    useEffect(() => {
        let alive = true;
        ListMachines({ active: 'true', limit: 200 })
            .then((data) => {
                if (alive) {
                    setMachines(ExtractListRows(data, ['machines']));
                }
            })
            .catch(() => {
                if (alive) {
                    setMachines([]);
                }
            });
        return () => {
            alive = false;
        };
    }, []);

    const load = useCallback(async () => {
        setLoadError(null);
        try {
            const offset = (Math.max(1, page) - 1) * Math.max(1, pageSize);
            const data = await ListProductionOrders({
                limit: pageSize,
                offset,
                status: status || undefined,
                machine_code: machineCode || undefined,
            });
            const list = ExtractListRows(data, ['production_orders']);
            setRows(list);
            setTotal(ExtractPaging(data, { total: list.length, limit: pageSize, offset }).total);
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRows([]);
        } finally {
            setHasLoadedOnce(true);
        }
    }, [page, pageSize, status, machineCode]);

    useEffect(() => {
        load();
    }, [load]);

    // Algo mudou numa OP (ficha ou criação): lista e painel juntos.
    const refreshAll = useCallback(() => {
        load();
        loadSummary();
    }, [load, loadSummary]);

    const changeFilter = (setter) => (value) => {
        setter(value);
        setPage(1);
    };

    const columns = useMemo(
        () => [
            {
                key: 'code',
                label: label('wms.field.production_order_code'),
                getValue: (row) => row.code,
                render: (row) => <Id value={row.code} className="font-medium" />,
            },
            {
                key: 'status',
                label: label('wms.field.status'),
                getValue: (row) => label(`wms.production_order_status.${row.status}`),
                render: (row) => (
                    <Badge variant={ProductionOrderStatusVariant(row.status)}>
                        {label(`wms.production_order_status.${row.status}`)}
                    </Badge>
                ),
            },
            {
                key: 'sku_in',
                label: label('wms.field.product_in'),
                getValue: (row) => `${row.sku_in || ''} ${row.product_in_name || ''}`.trim(),
                render: (row) =>
                    row.sku_in ? (
                        <span className="block min-w-0">
                            <Sku value={row.sku_in} className="text-xs font-medium" />
                            <span className="block truncate text-xs text-mono-500 dark:text-mono-400">
                                {row.product_in_name || ''}
                            </span>
                        </span>
                    ) : (
                        BLANK_CELL
                    ),
            },
            {
                key: 'sku',
                label: label('wms.field.product_out'),
                getValue: (row) => `${row.sku || ''} ${row.product_name || ''}`.trim(),
                render: (row) => (
                    <span className="block min-w-0">
                        <Sku value={row.sku} className="text-xs font-medium" />
                        <span className="block truncate text-xs text-mono-500 dark:text-mono-400">
                            {row.product_name || ''}
                        </span>
                    </span>
                ),
            },
            {
                key: 'machine_code',
                label: label('wms.field.machine'),
                getValue: (row) => row.machine_code || '',
                render: (row) => (row.machine_code ? <Mono>{row.machine_code}</Mono> : BLANK_CELL),
            },
            {
                key: 'kit_size',
                label: label('wms.field.kit_size'),
                align: 'right',
                getValue: (row) => row.kit_size ?? 0,
                render: (row) => (row.kit_size ? <Mono>{row.kit_size}</Mono> : BLANK_CELL),
            },
            {
                key: 'started_at',
                label: label('wms.field.production_day'),
                getValue: (row) => row.started_at || row.created_at || '',
                render: (row) => <DateTimeText value={row.started_at || row.created_at} relative />,
            },
        ],
        [label],
    );

    const listContent = !hasLoadedOnce ? (
        <SkeletonTable columns={6} rows={8} />
    ) : loadError ? (
        <ErrorCard
            title={label('wms.production.orders_title')}
            message={`${label('wms.common.load_error')}\n${loadError}`}
            action={label('wms.common.retry')}
            actionFn={load}
        />
    ) : (
        <StandardTable
            rows={rows}
            columns={columns}
            rowKey={(row, index) => row.id || row.code || index}
            onRowClick={(row) => setSelectedId(row.id)}
            countLabel={label('wms.production.count')}
            exportFileName="wms-ordens-producao"
            emptyMessage={label('wms.production.empty')}
            searchPlaceholder={label('wms.production.search_placeholder')}
            paginationMode="server"
            totalItems={total}
            currentPage={page}
            onPageChange={setPage}
            itemsPerPage={pageSize}
            onItemsPerPageChange={setPageSize}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
    );

    return (
        <section className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h2 className="text-lg font-semibold text-black dark:text-white">
                        {label('wms.production.orders_title')}
                    </h2>
                    <p className="max-w-3xl text-sm text-mono-600 dark:text-mono-400">
                        {label('wms.production.orders_description')}
                    </p>
                </div>
                <Button onClick={() => setCreateOpen(true)} className="w-full sm:w-auto">
                    <PlusCircle size={18} className="mr-2" />
                    {label('wms.production.new_order')}
                </Button>
            </div>

            <ProductionOrdersSummary summary={summary} label={label} />

            <div className="grid grid-cols-1 gap-4 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950 md:grid-cols-2">
                <Select
                    label={label('wms.field.status')}
                    value={status}
                    onChange={(event) => changeFilter(setStatus)(event.target.value)}
                >
                    <option value="">{label('wms.production.status_all')}</option>
                    {PRODUCTION_ORDER_STATUSES.map((option) => (
                        <option key={option} value={option}>
                            {label(`wms.production_order_status.${option}`)}
                        </option>
                    ))}
                </Select>
                <Select
                    label={label('wms.field.machine')}
                    value={machineCode}
                    onChange={(event) => changeFilter(setMachineCode)(event.target.value)}
                >
                    <option value="">{label('wms.production.machine_all')}</option>
                    {machines.map((machine) => (
                        <option key={machine.id} value={machine.code}>
                            {`${machine.code} · ${machine.sector}`}
                        </option>
                    ))}
                </Select>
            </div>

            <div className="min-w-0">{listContent}</div>

            <Modal
                isOpen={Boolean(selectedId)}
                onClose={() => setSelectedId(null)}
                title={label('wms.production.detail_title')}
                size="4xl"
            >
                {selectedId ? (
                    <ProductionOrderDetailPanel
                        orderId={selectedId}
                        onChanged={refreshAll}
                        onOpenLot={(lot) => {
                            setSelectedId(null);
                            onOpenLot?.(lot);
                        }}
                    />
                ) : null}
            </Modal>

            <ProductionOrderCreateModal
                isOpen={isCreateOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => {
                    setPage(1);
                    refreshAll();
                }}
            />
        </section>
    );
};

export default ProductionOrdersManager;
